// src/pages/InventoryPage.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Search,
  Filter,
  Download,
  X,
  ListChecks,
  Trash,
} from "lucide-react";
import toast from "react-hot-toast";
import DataTable from "../components/data-table/DataTable";
import { getProducts } from "../api/products";
import { getCategories, getSubCategories, listSubCategories } from "../api/categories";
import { getMe } from "../api/users";
import { listStoreLocations } from "../api/storeLocations";
import { useNavigate } from "react-router-dom";
import StoreScopeFilter from "../components/common/StoreScopeFilter";
import { useStoreScopeFilter } from "../hooks/useStoreScopeFilter";
import { IDR as formatIDR } from "../lib/fmt";
import { hasManagementAccess } from "../utils/roles";
import { getAuth } from "../api/auth";

const PER_PAGE = 10;
const STORAGE_KEY = "inventory_store_id";
const PARENT_STORAGE_KEY = "inventory_parent_store_id";

const toNum = (v) =>
  Number(v ?? 0).toLocaleString("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
const labelFromMap = (m, id) =>
  id == null
    ? "-"
    : m.get(id) ?? m.get(Number(id)) ?? m.get(String(id)) ?? String(id);

const categoryLabel = (row, catMap) =>
  row?.category?.name ||
  row?.category_name ||
  labelFromMap(catMap, row?.category_id);

const subCategoryLabel = (row, subMap) =>
  row?.sub_category?.name ||
  row?.subCategory?.name ||
  row?.sub_category_name ||
  labelFromMap(subMap, row?.sub_category_id);

const stockQty = (row) => Number(row?.stock ?? row?.stock_total ?? 0) || 0;
const lineTotal = (row) => stockQty(row) * (Number(row?.price) || 0);
const uomLabel = (row) =>
  row?.unit?.name || row?.unit_name || row?.uom || "-";

export default function InventoryProductsPage() {
  const navigate = useNavigate();

  // ===== USER / STORE SCOPE =====
  const [me, setMe] = useState(() => getAuth()?.user ?? null);
  const [stores, setStores] = useState([]);

  const {
    parentFilterId,
    storeFilterId,
    effectiveStoreId,
    canPickStore,
    activeStoreLabel,
    handleParentChange,
    handleBranchChange,
  } = useStoreScopeFilter({
    branchStorageKey: STORAGE_KEY,
    parentStorageKey: PARENT_STORAGE_KEY,
    me,
    stores,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const profile = await getMe();
        if (cancelled) return;
        setMe(profile || getAuth()?.user || null);
      } catch {
        // Keep the session user from localStorage so HQ store pickers
        // still appear if /api/me is slow or fails.
        if (!cancelled) setMe((prev) => prev || getAuth()?.user || null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!canPickStore) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await listStoreLocations({ page: 1, per_page: 200 });
        if (!cancelled) setStores(res?.items || []);
      } catch {
        if (!cancelled) setStores([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canPickStore]);

  // ===== server data & meta =====
  const [rawRows, setRawRows] = useState([]);
  const [serverMeta, setServerMeta] = useState({
    current_page: 1,
    last_page: 1,
    per_page: PER_PAGE,
    total: 0,
  });
  const [loading, setLoading] = useState(false);

  // ===== client states =====
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const onParentStoreChange = useCallback(
    (nextParentId) => {
      handleParentChange(nextParentId);
      setCurrentPage(1);
    },
    [handleParentChange]
  );

  const onBranchStoreChange = useCallback(
    (nextBranchId) => {
      handleBranchChange(nextBranchId);
      setCurrentPage(1);
    },
    [handleBranchChange]
  );

  const [showFilters, setShowFilters] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const [subCategoryId, setSubCategoryId] = useState("");

  // anchor popover untuk filter category/subcategory
  const filterBtnRef = useRef(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });

  // options category/subcategory
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  // client filter aktif untuk kategori/subkategori
  const clientFilterActive = Boolean(categoryId || subCategoryId);

  // ===== load categories & subcategories for the selected store =====
  useEffect(() => {
    if (effectiveStoreId == null) {
      setCategories([]);
      setSubCategories([]);
      return;
    }

    let cancel = false;

    const toArray = (res) => {
      const payload = res?.data ?? res;
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.data)) return payload.data;
      if (Array.isArray(payload?.items)) return payload.items;
      return [];
    };

    const fetchFresh = async () => {
      try {
        const storeParams = {
          store_location_id: effectiveStoreId,
          per_page: 1000,
        };
        const [catRes, subRes] = await Promise.all([
          getCategories(storeParams),
          listSubCategories(storeParams),
        ]);
        if (cancel) return;
        const cats = toArray(catRes);
        const subs = (subRes?.items || toArray(subRes) || []).map((s) => ({
          id: s.id,
          name: s.name,
          category_id: s.category_id,
        }));
        setCategories(cats);
        setSubCategories(subs);
      } catch {
        if (!cancel) {
          setCategories([]);
          setSubCategories([]);
        }
      }
    };

    fetchFresh();

    return () => {
      cancel = true;
    };
  }, [effectiveStoreId]);

  // ===== FETCH LIST (server paging vs client filter mode) =====
  useEffect(() => {
    // HQ must pick a branch before we can show per-store stock.
    // Use effectiveStoreId (not parent-only) so a saved branch still loads
    // even if the parent dropdown has not been inferred yet.
    if (canPickStore && effectiveStoreId == null) {
      setRawRows([]);
      setServerMeta({
        current_page: 1,
        last_page: 1,
        per_page: PER_PAGE,
        total: 0,
      });
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);

    const baseForClient = {
      page: 1,
      per_page: 1000,
      search: searchTerm.trim() || undefined,
      sort: sortKey || undefined,
      dir: sortKey ? sortDir : undefined,
    };

    const baseForServer = {
      page: currentPage,
      per_page: PER_PAGE,
      search: searchTerm.trim() || undefined,
      category_id: categoryId || undefined,
      sub_category_id: subCategoryId || undefined,
      sort: sortKey || undefined,
      dir: sortKey ? sortDir : undefined,
    };

    const params = {
      ...(clientFilterActive ? baseForClient : baseForServer),
      ...(effectiveStoreId != null ? { store_location_id: effectiveStoreId } : {}),
    };

    getProducts(params, controller.signal)
      .then(({ items, meta }) => {
        setRawRows(items || []);
        setServerMeta(
          meta || {
            current_page: 1,
            last_page: 1,
            per_page: PER_PAGE,
            total: (items || []).length,
          }
        );
      })
      .catch((err) => {
        const isCanceled =
          err?.name === "CanceledError" || err?.code === "ERR_CANCELED";
        if (!isCanceled) toast.error("Gagal memuat daftar produk");
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [
    searchTerm,
    sortKey,
    sortDir,
    currentPage,
    clientFilterActive,
    categoryId,
    subCategoryId,
    effectiveStoreId,
    canPickStore,
  ]);

  // ===== maps for labels =====
  const catMap = useMemo(
    () => new Map((categories || []).map((c) => [c.id, c.name])),
    [categories]
  );
  const subMap = useMemo(
    () => new Map((subCategories || []).map((s) => [s.id, s.name])),
    [subCategories]
  );

  // ===== client-side filter/sort (saat filter aktif) =====
  const filteredSorted = useMemo(() => {
    let list = rawRows;

    if (clientFilterActive) {
      if (categoryId)
        list = list.filter(
          (r) => String(r.category_id) === String(categoryId)
        );
      if (subCategoryId)
        list = list.filter(
          (r) => String(r.sub_category_id) === String(subCategoryId)
        );

      if (searchTerm.trim()) {
        const q = searchTerm.trim().toLowerCase();
        list = list.filter(
          (r) =>
            String(r.name || "").toLowerCase().includes(q) ||
            String(r.sku || "").toLowerCase().includes(q)
        );
      }

      if (sortKey) {
        const dir = sortDir === "desc" ? -1 : 1;
        list = [...list].sort((a, b) => {
          const pick = (row) => {
            if (sortKey === "category_id")
              return categoryLabel(row, catMap);
            if (sortKey === "sub_category_id")
              return subCategoryLabel(row, subMap);
            return row[sortKey];
          };
          const va = pick(a);
          const vb = pick(b);
          if (va == null && vb == null) return 0;
          if (va == null) return -1 * dir;
          if (vb == null) return 1 * dir;
          if (typeof va === "number" && typeof vb === "number")
            return (va - vb) * dir;
          return String(va).localeCompare(String(vb)) * dir;
        });
      }
    }

    return list;
  }, [
    rawRows,
    clientFilterActive,
    categoryId,
    subCategoryId,
    searchTerm,
    sortKey,
    sortDir,
    catMap,
    subMap,
  ]);

  // ===== paginate & NORMALIZE meta =====
  const { pageRows, meta } = useMemo(() => {
    if (clientFilterActive) {
      const total = filteredSorted.length;
      const per = PER_PAGE;
      const last = Math.max(1, Math.ceil(total / per));
      const curr = Math.min(currentPage, last);
      const startIdx = (curr - 1) * per;
      const endIdx = startIdx + per;
      return {
        pageRows: filteredSorted.slice(startIdx, endIdx),
        meta: {
          current_page: curr,
          last_page: last,
          per_page: per,
          total,
        },
      };
    }

    const per = serverMeta?.per_page ?? PER_PAGE;
    const total = serverMeta?.total ?? rawRows.length;
    const last =
      serverMeta?.last_page ??
      Math.max(1, Math.ceil(total / Math.max(1, per)));
    const curr = serverMeta?.current_page ?? currentPage;

    return {
      pageRows: rawRows,
      meta: {
        ...serverMeta,
        per_page: per,
        total,
        last_page: last,
        current_page: curr,
      },
    };
  }, [clientFilterActive, filteredSorted, rawRows, serverMeta, currentPage]);

  // ===== sorting =====
  const handleSort = useCallback(
    (key) => {
      if (!key) return;
      if (sortKey === key)
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      else {
        setSortKey(key);
        setSortDir("asc");
      }
      setCurrentPage(1);
    },
    [sortKey]
  );

  // ===== Columns =====
  const columns = [
    {
      key: "name",
      header: "Product",
      sticky: "left",
      width: "240px",
      className: "font-medium",
      cell: (row) => <span className="text-gray-900">{row.name}</span>,
    },
    {
      key: "sku",
      header: "SKU",
      width: "160px",
      cell: (row) => <span className="text-gray-700">{row.sku || "-"}</span>,
    },
    {
      key: "uom",
      header: "UOM",
      width: "100px",
      cell: (row) => <span className="text-gray-700">{uomLabel(row)}</span>,
    },
    {
      key: "category_id",
      header: "Category",
      width: "200px",
      cell: (row) => <span>{categoryLabel(row, catMap)}</span>,
    },
    {
      key: "sub_category_id",
      header: "Sub Category",
      width: "220px",
      cell: (row) => (
        <span className="text-gray-700">
          {subCategoryLabel(row, subMap)}
        </span>
      ),
    },
    {
      key: "stock",
      header: "Stock",
      align: "right",
      width: "120px",
      cell: (row) => <span>{toNum(row.stock ?? row.stock_total ?? 0)}</span>,
    },
    {
      key: "price",
      header: "Price",
      align: "right",
      width: "140px",
      className: "hidden sm:table-cell",
      cell: (row) => (
        <span className="font-medium">{formatIDR(row.price)}</span>
      ),
    },
    {
      key: "total",
      header: "Total",
      align: "right",
      width: "150px",
      cell: (row) => (
        <span className="font-semibold text-slate-900">
          {formatIDR(lineTotal(row))}
        </span>
      ),
    },
    {
      key: "__actions",
      header: "Actions",
      sticky: "right",
      className: "sticky right-0 z-20 bg-white w-[92px]",
      cell: (row) => (
        <div
          className="sticky right-0 z-20 bg-white flex items-center justify-end gap-2 pr-2"
          style={{ boxShadow: "-6px 0 6px -6px rgba(0,0,0,.12)" }}
        >
          <button
            onClick={() =>
              navigate(`/inventory/products/${row.id}`, {
                state: { product: row, storeId: effectiveStoreId },
              })
            }
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
            title="Detail produk"
          >
            Detail
          </button>
        </div>
      ),
    },
  ];

  // ===== filter popover helpers =====
  const toggleFilter = () => {
    if (!showFilters) {
      const el = filterBtnRef.current;
      if (el) {
        const r = el.getBoundingClientRect();
        const gap = 8,
          width = 320;
        const left = Math.min(
          Math.max(r.right - width, 8),
          window.innerWidth - width - 8
        );
        const top = Math.min(r.bottom + gap, window.innerHeight - 8);
        setPopoverPos({ top, left });
      }
    }
    setShowFilters((s) => !s);
  };

  // ===== Export Excel (all pages for the current store + filters) =====
  const exportExcel = async () => {
    if (canPickStore && effectiveStoreId == null) {
      toast.error("Pilih cabang terlebih dahulu.");
      return;
    }
    try {
      toast.loading("Menyiapkan Excel...", { id: "exp" });
      const XLSX = await import("xlsx");

      const CHUNK_SIZE = 100;
      const MAX_ROWS = 50000;
      const list = [];
      let page = 1;
      let lastPage = 1;
      do {
        const { items, meta: m } = await getProducts({
          page,
          per_page: CHUNK_SIZE,
          search: searchTerm.trim() || undefined,
          category_id: categoryId || undefined,
          sub_category_id: subCategoryId || undefined,
          sort: sortKey || undefined,
          dir: sortKey ? sortDir : undefined,
          ...(effectiveStoreId != null
            ? { store_location_id: effectiveStoreId }
            : {}),
        });
        list.push(...(items || []));
        lastPage = Number(m?.last_page ?? 1);
        page += 1;
        if (list.length >= MAX_ROWS) break;
      } while (page <= lastPage);

      const data = list;

      const headers = [
        "SKU",
        "Product",
        "UOM",
        "Category",
        "Sub Category",
        "Stock",
        "Price",
        "Total",
      ];
      const rows = (data || []).map((r) => [
        r.sku || "-",
        r.name || "-",
        uomLabel(r),
        categoryLabel(r, catMap),
        subCategoryLabel(r, subMap),
        stockQty(r),
        Number(r.price) || 0,
        lineTotal(r),
      ]);

      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
      const currencyFmt = '"Rp"#,##0';
      const stockFmt = "#,##0.####";
      for (let R = 1; R <= range.e.r; R += 1) {
        const stockCell = ws[XLSX.utils.encode_cell({ r: R, c: 5 })];
        if (stockCell) {
          stockCell.t = "n";
          stockCell.z = stockFmt;
        }
        const priceCell = ws[XLSX.utils.encode_cell({ r: R, c: 6 })];
        if (priceCell) {
          priceCell.t = "n";
          priceCell.z = currencyFmt;
        }
        const totalCell = ws[XLSX.utils.encode_cell({ r: R, c: 7 })];
        if (totalCell) {
          totalCell.t = "n";
          totalCell.z = currencyFmt;
        }
      }
      ws["!cols"] = [
        { wch: 16 },
        { wch: 36 },
        { wch: 10 },
        { wch: 18 },
        { wch: 18 },
        { wch: 12 },
        { wch: 16 },
        { wch: 16 },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Inventory Products");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      XLSX.writeFile(wb, `inventory-products-${ts}.xlsx`);
      toast.success(`Excel berhasil diunduh (${list.length} produk)`, { id: "exp" });
    } catch (err) {
      console.error(err);
      toast.error("Gagal mengekspor Excel", { id: "exp" });
    }
  };

  const appliedFilterCount = (categoryId ? 1 : 0) + (subCategoryId ? 1 : 0);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-800">Inventory Products</h2>
        <div className="flex flex-wrap items-center gap-3">
          <StoreScopeFilter
            stores={stores}
            me={me}
            parentId={parentFilterId}
            branchId={storeFilterId}
            onParentChange={onParentStoreChange}
            onBranchChange={onBranchStoreChange}
            canPickStore={canPickStore}
            lockedLabel={activeStoreLabel}
          />
        </div>
      </div>

      {canPickStore && effectiveStoreId == null && (
        <div className="mt-4 p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm">
          Pilih parent store dan branch store untuk melihat stok inventory cabang tersebut.
        </div>
      )}

      {/* Controls */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mt-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search name / SKU..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-9 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            />
            {searchTerm && (
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => {
                  setSearchTerm("");
                  setCurrentPage(1);
                }}
                title="Clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filter + Export + Reconciliation */}
          <div className="flex items-center gap-2">
            <button
              ref={filterBtnRef}
              onClick={toggleFilter}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Filter className="w-4 h-4" />
              Filter
              {appliedFilterCount > 0 && (
                <span className="ml-1 px-2 py-0.5 text-xs bg-blue-500 text-white rounded-full">
                  {appliedFilterCount}
                </span>
              )}
            </button>

            <button
              onClick={exportExcel}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              title="Export Excel"
            >
              <Download className="w-4 h-4" />
              Export
            </button>

            {/* Waste / write-off */}
            <button
              onClick={() => navigate("/inventory/write-off")}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-rose-700 bg-white border border-rose-300 rounded-lg hover:bg-rose-50"
              title="Waste / Write-off"
            >
              <Trash className="w-4 h-4" />
              Waste
            </button>

            {hasManagementAccess(me?.role) && (
              <button
                onClick={() => navigate("/inventory/reconciliation")}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                title="Stock Reconciliation"
              >
                <ListChecks className="w-4 h-4" />
                Reconciliation
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg mt-4">
        <div className="relative w-full overflow-x-auto">
          <div className="inline-block align-middle w-full">
            <DataTable
              columns={columns}
              data={pageRows}
              loading={loading}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={handleSort}
              meta={meta}
              currentPage={meta.current_page}
              onPageChange={(p) => setCurrentPage(p)}
              stickyHeader
              getRowKey={(row, i) => row.id ?? row.sku ?? i}
              className="border-0 shadow-none"
            />
          </div>
        </div>
      </div>

      {/* Overlay filter */}
      {showFilters && (
        <div className="fixed inset-0 z-40" onMouseDown={() => setShowFilters(false)} />
      )}

      {/* Filter Popover */}
      {showFilters && (
        <div
          className="fixed z-50 w-80 bg-white rounded-lg shadow-lg border border-gray-200"
          style={{ top: popoverPos.top, left: popoverPos.left }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Filters</h3>
            <button
              onClick={() => setShowFilters(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  setSubCategoryId("");
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">All</option>
                {(categories || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sub Category</label>
              <select
                value={subCategoryId}
                onChange={(e) => {
                  setSubCategoryId(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">All</option>
                {(subCategories || [])
                  .filter(
                    (s) =>
                      !categoryId || String(s.category_id) === String(categoryId)
                  )
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <button
              onClick={() => {
                setCategoryId("");
                setSubCategoryId("");
                setCurrentPage(1);
              }}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Clear All
            </button>
            <button
              onClick={() => setShowFilters(false)}
              className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
